import { languageMap } from "screens/patientInfo/helpers/language";
import { constants } from "../constants/redux-constants";
import { AVAILABLE_LANGUAGES } from "config/helpers/multilingual_helper";
import { cdssCustomTitleValidator } from "config/helpers/common_helper";

const initialState = {
  token: null,
  recordingByEncounter: {},
  isEnding: false,
  language: AVAILABLE_LANGUAGES?.[0],
  hasErrorEncountered: false,
  lastSyncedTimeStamp: null,
  cdss: null,
  cdssTabProps: {
    isSet: false,
    title: null,
    color: null,
  },
  loading: {
    recording: false,
    paused: false,
  },
  progressState: {},
  autoStart: false,
  isRecordingInProgress: false,
  endLoading: false,
  zoomCallOngoing: false,
  assignedDoctorUid: null,
};

const recognizerReducer = (state = initialState, action) => {
  switch (action.type) {
    case constants?.RECOGNIZER?.LANGUAGE:
      return {
        ...state,
        language: action?.payload || state?.language,
      };
    case constants?.RECOGNIZER?.SET_STT_TOKEN:
      return {
        ...state,
        token: action?.payload,
        hasErrorEncountered: false,
        isEnding: false,
      };

    case constants.RECOGNIZER.START_RECORDING: {
      const { patientId, encounterId, conversationId, ownerUid } = action.payload;
      const key = `${patientId}-${encounterId}`;
      const existing = state.recordingByEncounter[key] || {};
      return {
        ...state,
        recordingByEncounter: {
          ...state.recordingByEncounter,
          [key]: {
            ...existing,
            recording: true,
            paused: false,
            hasErrorEncountered: false,
            conversationId,
            ownerUid: ownerUid ?? existing.ownerUid,
          },
        },
      };
    }

    case constants.RECOGNIZER.SET_NOT_OWNER: {
      const { patientId, encounterId } = action.payload;
      const key = `${patientId}-${encounterId}`;
      return {
        ...state,
        recordingByEncounter: {
          ...state.recordingByEncounter,
          [key]: {
            ...(state.recordingByEncounter[key] || {}),
            notOwner: true,
          },
        },
      };
    }
    case constants.RECOGNIZER.START_RECORDING_LOADING:
      return {
        ...state,
        loading: {
          ...state?.loading,
          recording: action.payload,
        },
      };

    case constants.RECOGNIZER.PAUSE_RECORDING: {
      const { patientId, encounterId } = action.payload;
      const key = `${patientId}-${encounterId}`;
      return {
        ...state,
        recordingByEncounter: {
          ...state.recordingByEncounter,
          [key]: {
            ...(state.recordingByEncounter[key] || {}),
            recording: false,
            paused: true,
          },
        },
      };
    }

    case constants.RECOGNIZER.RESUME_RECORDING: {
      const { patientId, encounterId } = action.payload;
      const key = `${patientId}-${encounterId}`;
      return {
        ...state,
        recordingByEncounter: {
          ...state.recordingByEncounter,
          [key]: {
            ...(state.recordingByEncounter[key] || {}),
            recording: true,
            paused: false,
          },
        },
      };
    }

    case constants.RECOGNIZER.STOP_RECORDING: {
      const { patientId, encounterId } = action.payload;
      const key = `${patientId}-${encounterId}`;
      const { [key]: removed, ...rest } = state.recordingByEncounter;
      return {
        ...state,
        recordingByEncounter: rest,
      };
    }

    case constants.RECOGNIZER.HAS_ERROR_ENCOUNTERED: {
      const { patientId, encounterId, error } = action.payload;
      const key = `${patientId}-${encounterId}`;
      return {
        ...state,
        hasErrorEncountered: error,
        recordingByEncounter: {
          ...state.recordingByEncounter,
          [key]: {
            ...(state.recordingByEncounter[key] || {}),
            recording: true,
            paused: false,
          },
        },
      };
    }

    case constants.RECOGNIZER.CONVERSATION_ENDING:
      return {
        ...state,
        isEnding: true,
      };

    case constants.RECOGNIZER.UPDATE_CDSS_DATA:
      if (action.payload?.lastComputedAt !== state.lastSyncedTimeStamp) {
        return {
          ...state,
          lastSyncedTimeStamp: action.payload?.lastComputedAt,
          cdss: action.payload?.result || {},
        };
      }

      return state;
    case constants.RECOGNIZER.RESET_RECOGNIZER_FOR_ENCOUNTER:
      return {
        ...state,
        cdss: null,
        token: null,
        lastSyncedTimeStamp: null,
        cdssTabProps: {
          isSet: false,
          title: null,
          color: null,
        },
        // keep per-encounter states as is
        recordingByEncounter: state.recordingByEncounter,
        progressState: state.progressState,
      };

    case constants.RECOGNIZER.AUTO_START:
      return {
        ...state,
        autoStart: action.payload,
      };

    case constants.RECOGNIZER.START_RECORDING_IN_PROGRESS:
      return {
        ...state,
        isRecordingInProgress: action.payload,
      };

    case constants.RECOGNIZER.SET_END_LOADING:
      return {
        ...state,
        endLoading: action.payload,
      };

    case constants.RECOGNIZER.SET_CDSS_TITLE:
      const { color, isValid } = cdssCustomTitleValidator(action.payload);
      return {
        ...state,
        cdssTabProps: {
          isSet: isValid,
          title: isValid ? action.payload : null,
          color: isValid ? color : null,
        },
      };

    case constants.RECOGNIZER.UNSET_CDSS_TITLE:
      return {
        ...state,
        cdssTabProps: {
          isSet: false,
          title: null,
          color: null,
        },
      };

    case constants.RECOGNIZER.UPDATE_PROGRESS_STATE: {
      const { patientId, encounterId, ...progressData } = action.payload;
      const key = `${patientId}-${encounterId}`;
      return {
        ...state,
        progressState: {
          ...state.progressState,
          [key]: {
            patientId,
            encounterId,
            isProgressReady: progressData.isProgressReady ?? false,
            ...progressData,
          },
        },
      };
    }

    case constants.RECOGNIZER.ZOOM_CALL_ONGOING:
      return {
        ...state,
        zoomCallOngoing: action.payload,
      };

    case constants.RECOGNIZER.RESET_PROGRESS_STATE: {
      const { patientId, encounterId } = action.payload;
      const key = `${patientId}-${encounterId}`;
      const { [key]: removed, ...rest } = state.progressState;
      return {
        ...state,
        progressState: rest,
      };
    }

    case constants.RECOGNIZER.SET_ASSIGNED_DOCTOR_UID:
      return {
        ...state,
        assignedDoctorUid: action.payload,
      };

    case constants.RECOGNIZER.RESET_RECOGNIZER:
      return { ...initialState };

    default:
      return state;
  }
};

export default recognizerReducer;
